import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@journ/ui/accordion'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { useState } from 'react'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import { session$ } from '@/features/auth/store'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import { useTimeOffRequest } from '../hooks/use-time-off-request'
import { useTimeOffActions } from '../hooks/use-time-off-actions'
import { refreshTimeOffList } from '../store'
import type { ApprovalStage } from '../domain/timeoff'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import { getApprovalStage } from './get-approval-stage'
import { useMemberParams } from './use-member-params'

const stageVariant: Record<ApprovalStage, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	pending_dept: 'secondary',
	pending_hr: 'outline',
	approved: 'default',
	rejected: 'destructive',
}

type FieldProps = {
	label: string
	value: string | null | undefined
	colSpan?: boolean
}

const Field = ({ label, value, colSpan }: FieldProps) => (
	<div className={colSpan ? 'col-span-2' : undefined}>
		<div className='text-[12px] mb-2 text-606060'>{label}</div>
		<div className='text-[14px]'>{value || '-'}</div>
	</div>
)

const TimeOffDetailSheet = () => {
	const content = useIntlayer('hr-page')
	const to = content.timeOff
	const { viewTimeOff, setParams } = useMemberParams()
	const { request, reload } = useTimeOffRequest(viewTimeOff ?? null)
	const { approveDept, rejectDept, approveHr, rejectHr, addNote } = useTimeOffActions()
	const { formatPreferenceDate, formatPreferenceDateTime } = useDateFormat()

	const [deptNote, setDeptNote] = useState('')
	const [deptRejectNote, setDeptRejectNote] = useState('')
	const [hrRejectNote, setHrRejectNote] = useState('')
	const [newNote, setNewNote] = useState('')
	const [submitting, setSubmitting] = useState(false)

	const user = useSelector(() => session$.user.get())
	const canApproveDept = usePermission('hr.time-off.approve-dept')
	const canApproveHr = usePermission('hr.time-off.approve-hr')
	const canNotes = usePermission('hr.time-off.notes')
	const canSeeNotes = canNotes || canApproveDept

	const isOpen = Boolean(viewTimeOff)
	const close = () => setParams({ viewTimeOff: null })

	if (!user) return null

	const actorId = user.id
	const actorName = user.fullName ?? user.email

	const stage = request ? getApprovalStage(request) : null

	const handleApproveDept = async () => {
		if (!request) return
		setSubmitting(true)
		try {
			await approveDept(request.id, actorId, actorName, deptNote.trim() || null)
			setDeptNote('')
			await reload()
			refreshTimeOffList()
		} finally {
			setSubmitting(false)
		}
	}

	const handleRejectDept = async () => {
		if (!request || !deptRejectNote.trim()) return
		setSubmitting(true)
		try {
			await rejectDept(request.id, actorId, actorName, deptRejectNote.trim())
			setDeptRejectNote('')
			await reload()
			refreshTimeOffList()
		} finally {
			setSubmitting(false)
		}
	}

	const handleApproveHr = async () => {
		if (!request) return
		setSubmitting(true)
		try {
			await approveHr(request.id, actorId, actorName)
			await reload()
			refreshTimeOffList()
		} finally {
			setSubmitting(false)
		}
	}

	const handleRejectHr = async () => {
		if (!request || !hrRejectNote.trim()) return
		setSubmitting(true)
		try {
			await rejectHr(request.id, actorId, actorName, hrRejectNote.trim())
			setHrRejectNote('')
			await reload()
			refreshTimeOffList()
		} finally {
			setSubmitting(false)
		}
	}

	const handleAddNote = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (!request || !newNote.trim()) return
		setSubmitting(true)
		try {
			await addNote(request.id, actorId, actorName, newNote.trim())
			setNewNote('')
			await reload()
			refreshTimeOffList()
		} finally {
			setSubmitting(false)
		}
	}

	const deptStatus = request?.deptApproval.status ?? 'pending'
	const step2Waiting = deptStatus === 'pending'

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={to.requestSheet.viewTitle.value}>
				{request && stage && (
					<div className='h-full flex flex-col min-h-0 -mx-6'>
						<div className='flex-1 overflow-y-auto scrollbar-hide min-h-0'>
							<SheetHeader className='flex justify-between items-center flex-row px-6 mb-4'>
								<div className='min-w-0 flex-1'>
									<h2 className='text-lg font-serif truncate'>{request.memberName}</h2>
									<p className='text-[13px] text-606060'>{to.requestType[request.type]?.value ?? request.type}</p>
								</div>
								<Badge variant={stageVariant[stage]}>{to.stage[stage]?.value ?? stage}</Badge>
							</SheetHeader>

							<div className='px-6'>
								<Accordion type='multiple' defaultValue={['dates', 'approval']} className='space-y-0'>
									<AccordionItem value='dates' className='border-b border-border'>
										<AccordionTrigger className='text-[16px] font-medium py-4'>{to.columns.dates}</AccordionTrigger>
										<AccordionContent>
											<div className='grid grid-cols-2 gap-4 pt-0'>
												<Field
													label={to.requestForm.startDate.value}
													value={`${formatPreferenceDate(request.startDate)}${request.halfDay ? ` (${request.halfDay.toUpperCase()})` : ''}`}
												/>
												<Field label={to.requestForm.endDate.value} value={formatPreferenceDate(request.endDate)} />
												{request.reason && <Field label={to.requestForm.reason.value} value={request.reason} colSpan />}
											</div>
										</AccordionContent>
									</AccordionItem>

									<AccordionItem value='approval' className='border-b border-border'>
										<AccordionTrigger className='text-[16px] font-medium py-4'>{to.columns.status}</AccordionTrigger>
										<AccordionContent>
											<div className='grid grid-cols-2 gap-4 pt-0'>
												<div>
													<div className='text-[12px] mb-2 text-606060'>{to.stage.pending_dept}</div>
													<div className='text-[14px]'>
														{request.deptApproval.status === 'pending'
															? '-'
															: `${request.deptApproval.actorName ?? ''}`}
													</div>
													{request.deptApproval.decidedAt && (
														<div className='text-[12px] text-606060 mt-1'>
															{formatPreferenceDateTime(request.deptApproval.decidedAt)}
														</div>
													)}
													{request.deptApproval.note && (
														<div className='text-[13px] text-606060 mt-1'>{request.deptApproval.note}</div>
													)}
												</div>

												<div className={step2Waiting ? 'opacity-40' : undefined}>
													<div className='text-[12px] mb-2 text-606060'>{to.stage.pending_hr}</div>
													<div className='text-[14px]'>
														{step2Waiting || request.hrApproval.status === 'pending'
															? '-'
															: `${request.hrApproval.actorName ?? ''}`}
													</div>
													{!step2Waiting && request.hrApproval.decidedAt && (
														<div className='text-[12px] text-606060 mt-1'>
															{formatPreferenceDateTime(request.hrApproval.decidedAt)}
														</div>
													)}
												</div>
											</div>
										</AccordionContent>
									</AccordionItem>

									{canApproveDept && stage === 'pending_dept' && (
										<AccordionItem value='action_dept' className='border-b border-border'>
											<AccordionTrigger className='text-[16px] font-medium py-4'>
												{to.actions.approveDept}
											</AccordionTrigger>
											<AccordionContent>
												<div className='space-y-4'>
													<div className='space-y-1.5'>
														<Label className='text-xs text-878787 font-normal'>{to.requestForm.reason}</Label>
														<Input
															value={deptNote}
															onChange={e => setDeptNote(e.target.value)}
															placeholder={to.teamLeaderNotes.placeholder.value}
															autoComplete='off'
														/>
													</div>
													<Button size='sm' onClick={handleApproveDept} disabled={submitting}>
														{to.actions.approveDept}
													</Button>

													<div className='border-t border-border pt-4 space-y-1.5'>
														<Label className='text-xs text-878787 font-normal'>
															{to.actions.rejectDept} — {to.requestForm.reason.value}
														</Label>
														<Input
															value={deptRejectNote}
															onChange={e => setDeptRejectNote(e.target.value)}
															placeholder={to.teamLeaderNotes.placeholder.value}
															autoComplete='off'
														/>
														<Button
															size='sm'
															variant='destructive'
															onClick={handleRejectDept}
															disabled={submitting || !deptRejectNote.trim()}
														>
															{to.actions.rejectDept}
														</Button>
													</div>
												</div>
											</AccordionContent>
										</AccordionItem>
									)}

									{canApproveHr && stage === 'pending_hr' && (
										<AccordionItem value='action_hr' className='border-b border-border'>
											<AccordionTrigger className='text-[16px] font-medium py-4'>
												{to.actions.approveHr}
											</AccordionTrigger>
											<AccordionContent>
												<div className='space-y-4'>
													<Button size='sm' onClick={handleApproveHr} disabled={submitting}>
														{to.actions.approveHr}
													</Button>

													<div className='border-t border-border pt-4 space-y-1.5'>
														<Label className='text-xs text-878787 font-normal'>
															{to.actions.rejectHr} — {to.requestForm.reason.value}
														</Label>
														<Input
															value={hrRejectNote}
															onChange={e => setHrRejectNote(e.target.value)}
															placeholder={to.teamLeaderNotes.placeholder.value}
															autoComplete='off'
														/>
														<Button
															size='sm'
															variant='destructive'
															onClick={handleRejectHr}
															disabled={submitting || !hrRejectNote.trim()}
														>
															{to.actions.rejectHr}
														</Button>
													</div>
												</div>
											</AccordionContent>
										</AccordionItem>
									)}

									{canSeeNotes && (
										<AccordionItem value='notes' className='border-b border-border'>
											<AccordionTrigger className='text-[16px] font-medium py-4'>
												{to.teamLeaderNotes.title}
											</AccordionTrigger>
											<AccordionContent>
												<div className='space-y-3'>
													{request.teamLeaderNotes.length === 0 ? (
														<p className='text-[14px] text-606060'>{to.teamLeaderNotes.empty}</p>
													) : (
														request.teamLeaderNotes.map(note => (
															<div key={note.id} className='border border-border p-3 space-y-1'>
																<div className='flex items-center justify-between'>
																	<p className='text-[13px] font-medium'>{note.authorName}</p>
																	<p className='text-[12px] text-606060'>{formatPreferenceDateTime(note.createdAt)}</p>
																</div>
																<p className='text-[13px] text-606060'>{note.note}</p>
															</div>
														))
													)}

													{canNotes && (
														<form onSubmit={handleAddNote} className='space-y-2'>
															<Input
																value={newNote}
																onChange={e => setNewNote(e.target.value)}
																placeholder={to.teamLeaderNotes.placeholder.value}
																autoComplete='off'
															/>
															<Button type='submit' size='sm' disabled={submitting || !newNote.trim()}>
																{to.teamLeaderNotes.add}
															</Button>
														</form>
													)}
												</div>
											</AccordionContent>
										</AccordionItem>
									)}
								</Accordion>
							</div>
						</div>
					</div>
				)}
			</SheetContent>
		</Sheet>
	)
}

export { TimeOffDetailSheet }
